import { z } from 'zod';
import {
  LanguageLevel,
  LearningView,
  NotificationChannel,
  LanguageLevelSchema,
  LearningViewSchema,
  NotificationChannelSchema,
  SubscriptionPlan,
  SubscriptionPlanSchema,
  SubscriptionStatus,
  SubscriptionStatusSchema,
} from './domain/common';
import { ValidationPatterns, SchemaUtils } from './shared/common';
import { WordSchemas } from './words';
export { LanguageLevel, LearningView, NotificationChannel };

const BaseUserSchema = z.object({
  id: z.string().min(1, 'User ID is required'),
  name: z.string().min(1).max(255),
  email: ValidationPatterns.email,
  picture: z.url().optional(),
  username: z.string().min(1).max(50).optional().nullable(),
  level: LanguageLevelSchema,
  language: z.string().min(2).max(10).default('dutch'),
  topics: z.array(z.string().min(1).max(100)).min(1).max(100),
  wordsPerDay: ValidationPatterns.wordsPerDay,
  deliveryHours: z.array(ValidationPatterns.hour).min(1).max(24),
  timezone: ValidationPatterns.timezone,
  learningView: LearningViewSchema,
  isActive: z.boolean().default(false),
  licenseKey: z.string().max(100).optional(),
  isTelegramActive: z.boolean().default(false),
  isWhatsAppActive: z.boolean().default(false),
  telegramId: z.number().int().positive().optional(),
  notificationChannel: NotificationChannelSchema,
  whatsappPhone: ValidationPatterns.phoneE164.optional(),
  plan: SubscriptionPlanSchema.default(SubscriptionPlan.Free),
  subscriptionStatus: SubscriptionStatusSchema.default(
    SubscriptionStatus.Canceled
  ),
  subscriptionExpiresAt: z.union([z.date(), z.string().datetime()]).optional(),
  stripeCustomerId: z.string().optional(),
  lastSentDate: z.union([z.date(), z.string().datetime()]).optional(),
  suspendedUntil: z.union([z.date(), z.string().datetime()]).optional(),
  ratingPreference: z.number().int().min(1).max(5).optional(),
  createdAt: ValidationPatterns.flexibleDate,
  updatedAt: ValidationPatterns.flexibleDate,
});
export const UserSchemas = {
  full: BaseUserSchema,
  profile: BaseUserSchema,
  create: BaseUserSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  }),
  update: BaseUserSchema.pick({
    name: true,
    level: true,
    language: true,
    wordsPerDay: true,
    topics: true,
    deliveryHours: true,
    timezone: true,
    learningView: true,
    notificationChannel: true,
    whatsappPhone: true,
  }).partial(),
  systemUpdate: z.object({
    name: z.string().min(1).max(255).optional(),
    username: z.string().min(1).max(50).optional().nullable(),
    level: LanguageLevelSchema.optional(),
    language: z.string().min(2).max(10).optional(),
    topics: z.array(z.string().min(1).max(100)).min(1).max(100).optional(),
    wordsPerDay: ValidationPatterns.wordsPerDay.optional(),
    deliveryHours: z.array(ValidationPatterns.hour).min(1).max(24).optional(),
    timezone: ValidationPatterns.timezone.optional(),
    learningView: LearningViewSchema.optional(),
    notificationChannel: NotificationChannelSchema.optional(),
    whatsappPhone: ValidationPatterns.phoneE164.optional(),
    plan: SubscriptionPlanSchema.optional(),
    subscriptionStatus: SubscriptionStatusSchema.optional(),
    subscriptionExpiresAt: z
      .union([z.date(), z.string().datetime()])
      .optional(),
    stripeCustomerId: z.string().optional(),
    isActive: z.boolean().optional(),
    licenseKey: z.string().max(100).optional(),
    telegramId: z.number().int().positive().optional(),
    isTelegramActive: z.boolean().optional(),
    isWhatsAppActive: z.boolean().optional(),
    lastSentDate: z.union([z.date(), z.string().datetime()]).optional(),
    suspendedUntil: z.union([z.date(), z.string().datetime()]).optional(),
    ratingPreference: z.number().int().min(1).max(5).optional(),
    updatedAt: ValidationPatterns.flexibleDate.optional(),
  }),
} as const;
export const UserProfileAPI = {
  request: z.void(),
  response: UserSchemas.profile,
};
export const UpdateUserAPI = {
  request: UserSchemas.update
    .extend({
      topics: z.array(z.string().min(1).max(100)).min(1).max(100).optional(),
    })
    .refine(
      data => {
        if (!data.topics && !data.deliveryHours) return true;
        return Object.keys(data).length > 0;
      },
      {
        message: 'At least one field must be provided for update',
      }
    ),
  response: z.object({ message: z.string() }),
};
export const UserWordsAPI = {
  request: z.object({
    topics: z.array(z.string().min(1).max(100)).min(1).max(100).optional(),
    wordsPerDay: ValidationPatterns.wordsPerDay.optional(),
    deliveryHours: z.array(ValidationPatterns.hour).min(1).max(24).optional(),
  }),
  response: z.object({ message: z.string() }),
};
export const DeleteUserAPI = {
  request: z.void(),
  response: z.object({ message: z.string() }),
};
export const UserSubscriptionAPI = {
  request: z.void(),
  response: z.object({
    user: UserSchemas.profile,
    subscription: z.object({
      plan: SubscriptionPlanSchema,
      status: SubscriptionStatusSchema,
      features: z.array(z.string()),
      limits: z.record(z.string(), z.number()),
    }),
  }),
};
export const UserNotificationAPI = {
  request: z.object({
    notificationChannel: NotificationChannelSchema.optional(),
    whatsappPhone: ValidationPatterns.phoneE164.optional(),
    deliveryHours: z.array(ValidationPatterns.hour).min(1).max(24).optional(),
  }),
  response: z.object({ message: z.string() }),
};
export const GenerateLicenseAPI = {
  request: z.union([z.object({}), z.undefined()]).transform(() => ({})),
  response: z.object({
    licenseKey: z.string().min(10).max(100),
  }),
};
export const GetUserWordsAPI = {
  request: z.object({
    level: z.enum(LanguageLevel).optional(),
    topic: z.string().min(1).max(100).optional(),
    search: z.string().min(1).max(100).optional(),
    page: z.number().int().min(1).default(1),
    limit: z.number().int().min(1).max(100).default(10),
    offset: z.number().int().min(0).optional(),
  }),
  response: SchemaUtils.createPaginatedResponse(WordSchemas.learned),
};
export type User = z.infer<typeof BaseUserSchema>;
export type ProfileUser = z.infer<typeof UserSchemas.profile>;
export type CreateUser = z.infer<typeof UserSchemas.create>;
export type UpdateUser = z.infer<typeof UserSchemas.update>;
export type SystemUpdateUser = z.infer<typeof UserSchemas.systemUpdate>;
export type UserProfileRequest = z.infer<typeof UserProfileAPI.request>;
export type UserProfileResponse = z.infer<typeof UserProfileAPI.response>;
export type UpdateUserRequest = z.infer<typeof UpdateUserAPI.request>;
export type UpdateUserResponse = z.infer<typeof UpdateUserAPI.response>;
export type UserWordsRequest = z.infer<typeof UserWordsAPI.request>;
export type UserWordsResponse = z.infer<typeof UserWordsAPI.response>;
export type DeleteUserRequest = z.infer<typeof DeleteUserAPI.request>;
export type DeleteUserResponse = z.infer<typeof DeleteUserAPI.response>;
export type UserSubscriptionRequest = z.infer<
  typeof UserSubscriptionAPI.request
>;
export type UserSubscriptionResponse = z.infer<
  typeof UserSubscriptionAPI.response
>;
export type UserNotificationRequest = z.infer<
  typeof UserNotificationAPI.request
>;
export type UserNotificationResponse = z.infer<
  typeof UserNotificationAPI.response
>;
export type GenerateLicenseRequest = z.infer<typeof GenerateLicenseAPI.request>;
export type GenerateLicenseResponse = z.infer<
  typeof GenerateLicenseAPI.response
>;
export type GetUserWordsRequest = z.infer<typeof GetUserWordsAPI.request>;
export type GetUserWordsResponse = z.infer<typeof GetUserWordsAPI.response>;
export const UserAPI = {
  profile: UserProfileAPI,
  update: UpdateUserAPI,
  words: UserWordsAPI,
  delete: DeleteUserAPI,
  subscription: UserSubscriptionAPI,
  notification: UserNotificationAPI,
  license: GenerateLicenseAPI,
  getUserWords: GetUserWordsAPI,
} as const;
interface MinimalUser {
  isActive?: boolean;
  licenseKey?: string;
  isTelegramActive?: boolean;
}
export function isActiveUser(user?: MinimalUser | null): boolean {
  return Boolean(user?.isActive && user?.licenseKey);
}
export function isTelegramActive(user?: MinimalUser | null): boolean {
  return isActiveUser(user) && Boolean(user?.isTelegramActive);
}
